import React from 'react';
import { CognitiveHealthSummary } from '../interfaces/types';
import { Star, ArrowUp, ArrowDown, Minus, Lightbulb, AlertTriangle, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface CognitiveInsightsProps {
  summary: CognitiveHealthSummary;
  weeklyScores: { date: string; score: number }[];
}

const CognitiveInsights: React.FC<CognitiveInsightsProps> = ({ summary, weeklyScores }) => {
  // Get trend icon
  const getTrendIcon = (direction: string): React.ReactNode => {
    switch (direction) {
      case 'IMPROVING':
        return <ArrowUp size={14} className="text-green-500" />;
      case 'DECLINING':
        return <ArrowDown size={14} className="text-red-500" />;
      case 'FLUCTUATING':
        return <AlertTriangle size={14} className="text-yellow-500" />;
      default:
        return <Minus size={14} className="text-gray-500" />;
    }
  };
  
  // Get risk level color
  const getRiskLevelColor = (riskLevel: string): string => {
    switch (riskLevel) {
      case 'LOW':
        return 'text-green-500';
      case 'MEDIUM':
        return 'text-yellow-500';
      case 'HIGH':
        return 'text-orange-500';
      case 'CRITICAL':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-5">
      <div className="flex items-center mb-4">
        <Lightbulb className="text-yellow-500 mr-2" />
        <h3 className="text-lg font-semibold">Cognitive Health Insights</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="col-span-1 bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-1">Cognitive Health Score</h4>
          <div className="flex items-end">
            <span className="text-3xl font-bold">{summary.overallScore}</span>
            <span className="text-sm text-gray-500 ml-1">/100</span>
          </div>
          <div className="flex items-center mt-1">
            <span className={`font-medium ${getRiskLevelColor(summary.riskLevel)}`}>
              {summary.riskLevel} risk
            </span>
            {summary.warningCount > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {summary.warningCount} {summary.warningCount === 1 ? 'warning' : 'warnings'}
              </span>
            )}
          </div>
        </div>
        
        <div className="col-span-2 bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-3">Recent Trends</h4>
          <div className="grid grid-cols-3 gap-3 mb-2">
            {Object.entries(summary.trends).slice(0, 3).map(([key, { direction, value }]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-gray-700 capitalize">{key}</span>
                <div className="flex items-center">
                  {getTrendIcon(direction)}
                  <span className={`text-sm ml-1 ${
                    direction === 'IMPROVING' ? 'text-green-500' :
                    direction === 'DECLINING' ? 'text-red-500' :
                    direction === 'FLUCTUATING' ? 'text-yellow-500' :
                    'text-gray-500'
                  }`}>
                    {value}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(summary.trends).slice(3, 6).map(([key, { direction, value }]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-gray-700 capitalize">{key}</span>
                <div className="flex items-center">
                  {getTrendIcon(direction)}
                  <span className={`text-sm ml-1 ${
                    direction === 'IMPROVING' ? 'text-green-500' :
                    direction === 'DECLINING' ? 'text-red-500' :
                    direction === 'FLUCTUATING' ? 'text-yellow-500' :
                    'text-gray-500'
                  }`}>
                    {value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {summary.significantChanges.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-3">Significant Changes</h4>
          <div className="space-y-2">
            {summary.significantChanges.map((change, index) => (
              <div 
                key={index} 
                className={`p-3 rounded-lg border ${
                  change.changePercent > 0 ? 'border-green-200 bg-green-50' : 
                  'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium text-sm">{change.metric}</span>
                    <p className="text-xs text-gray-600 mt-0.5">{change.timeframe}</p>
                  </div>
                  <div>
                    <span className={`text-sm font-bold ${
                      change.changePercent > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {change.changePercent > 0 ? '+' : ''}{change.changePercent}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="mb-6">
        <h4 className="text-sm font-medium mb-3">Weekly Cognitive Score Trend</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={weeklyScores}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="score" 
                stroke="#6366f1" 
                strokeWidth={2}
                activeDot={{ r: 6 }}
                name="Cognitive Score"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium mb-3">Key Strengths</h4>
          <div className="space-y-2">
            {summary.keyStrengths.map((strength, index) => (
              <div key={index} className="flex items-start p-2 bg-green-50 rounded-lg">
                <Star size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-green-800">{strength}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h4 className="text-sm font-medium mb-3">Areas for Monitoring</h4>
          <div className="space-y-2">
            {summary.keyWeaknesses.map((weakness, index) => (
              <div key={index} className="flex items-start p-2 bg-yellow-50 rounded-lg">
                <AlertTriangle size={16} className="text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-yellow-800">{weakness}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="mt-4 text-xs text-gray-500 flex items-center justify-end">
        <Clock size={12} className="mr-1" />
        <span>Last updated: {new Date(summary.lastUpdated).toLocaleString()}</span>
      </div>
    </div>
  );
};

export default CognitiveInsights; 